import { lazyLogSigsDatapoint, logUsage } from 'owa-analytics';
import { assertNever } from 'owa-assert';
import { ControlIcons } from 'owa-control-icons';
import { DisplayOption, FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import getImmutableEntryId from 'owa-immutable-id/lib/getImmutableEntryId';
import loc from 'owa-localize';
import { CloseText } from 'owa-locstrings/lib/strings/closetext.locstring.json';
import { downloadText } from 'owa-locstrings/lib/strings/downloadtext.locstring.json';
import { popoutsOpenInNewWindow } from 'owa-locstrings/lib/strings/popoutsopeninnewwindow.locstring.json';
import { lazyPrint } from 'owa-printableiframe-view';
import {
    ImagePreviewState,
    lazySaveHideReadingPaneOption,
    lazySetRightPaneDisplayAndUpdateSize,
    NativePdfPreviewState,
    PreviewPaneMode,
    RightPaneDisplay,
    SxSViewState,
} from 'owa-sxsdata';
import { isBrowserIE } from 'owa-user-agent/lib/userAgent';
import { SxSViewStringOverrides } from '../components/SxSView';
import { CustomData, SxSCustomData } from './combineCustomData';
import { downloadFileFromUrl } from 'download-file-from-url';
import { isFullScreenAvailable, requestFullScreen } from './fullScreenUtilities';
import {
    fullScreen,
    hideEmail as hideEmail_1,
    printAttachment,
    showEmail as showEmail_1,
} from './getCommandBarItems.locstring.json';
import {
    getDefaultPreviewPaneCommandBarItems,
    PreviewPaneCommandBarButton,
    PreviewPaneCommandBarItem,
} from './PreviewPaneCommandBarButton';

export default function getCommandBarItems(
    viewState: SxSViewState,
    getFullScreenElement: () => HTMLElement,
    onResize: () => void,
    pdfJsPrint: () => void,
    hasRightPane: boolean,
    hideCloseButton?: boolean,
    items?: PreviewPaneCommandBarItem[],
    onClose?: () => void,
    onPopOut?: () => void,
    getExtraCustomDataForDatapoint?: () => SxSCustomData,
    getItemIdForLogging?: null | (() => string),
    stringOverrides?: SxSViewStringOverrides,
    getDownloadUrl?: () => string,
    useNewButtonOrder?: boolean,
    getContainerElementForDownload?: () => HTMLElement,
    getCustomPropertiesForSigs?: null | (() => { FileType: string; Sender: string })
): FilterableMenuItem[] {
    if (!items) {
        items = getDefaultPreviewPaneCommandBarItems();
    }

    const menuItems: FilterableMenuItem[] = [];

    items.forEach(item => {
        if (item instanceof FilterableMenuItem) {
            menuItems.push(item);
        } else {
            switch (item) {
                case PreviewPaneCommandBarButton.Download:
                    menuItems.push(
                        getDownloadCommandItem(
                            viewState,
                            getExtraCustomDataForDatapoint,
                            getDownloadUrl,
                            getContainerElementForDownload,
                            getItemIdForLogging,
                            getCustomPropertiesForSigs
                        )
                    );
                    break;
                case PreviewPaneCommandBarButton.FullScreen:
                    menuItems.push(
                        new FilterableMenuItem({
                            [FilterableMenuItemArgs.name]: loc(fullScreen),
                            [FilterableMenuItemArgs.icon]: ControlIcons.FullScreen,
                            [FilterableMenuItemArgs.onClick]: () => {
                                requestFullScreen(getFullScreenElement());

                                logSxSCommandBarAction(
                                    'SxSFullScreen',
                                    null,
                                    getExtraCustomDataForDatapoint
                                );
                            },
                            [FilterableMenuItemArgs.shouldShow]: () =>
                                viewState.previewPane &&
                                isFullScreenAvailable(viewState.previewPane.mode),
                        })
                    );
                    break;
                case PreviewPaneCommandBarButton.Print:
                    menuItems.push(
                        new FilterableMenuItem({
                            [FilterableMenuItemArgs.name]: loc(printAttachment),
                            [FilterableMenuItemArgs.icon]: ControlIcons.Print,
                            [FilterableMenuItemArgs.onClick]: () => {
                                printImage();
                                logSxSPrint(viewState, getExtraCustomDataForDatapoint);
                            },
                            [FilterableMenuItemArgs.shouldShow]: () =>
                                viewState.previewPane &&
                                viewState.previewPane.mode === PreviewPaneMode.Image &&
                                viewState.previewPane.previewUrl !== null &&
                                !viewState.previewPane.printDisabled,
                            [FilterableMenuItemArgs.ariaLabel]: loc(printAttachment),
                        })
                    );
                    menuItems.push(
                        new FilterableMenuItem({
                            [FilterableMenuItemArgs.name]: loc(printAttachment),
                            [FilterableMenuItemArgs.icon]: ControlIcons.Print,
                            [FilterableMenuItemArgs.onClick]: () => {
                                window.open(
                                    (viewState.previewPane as NativePdfPreviewState).printUrl,
                                    '_blank'
                                );
                                logSxSPrint(viewState, getExtraCustomDataForDatapoint);
                            },
                            [FilterableMenuItemArgs.shouldShow]: () =>
                                isPdfPrintSupported() &&
                                viewState.previewPane &&
                                viewState.previewPane.mode === PreviewPaneMode.NativePdf &&
                                viewState.previewPane.printUrl !== null,
                            [FilterableMenuItemArgs.ariaLabel]: loc(printAttachment),
                        })
                    );
                    menuItems.push(
                        new FilterableMenuItem({
                            [FilterableMenuItemArgs.name]: loc(printAttachment),
                            [FilterableMenuItemArgs.icon]: ControlIcons.Print,
                            [FilterableMenuItemArgs.onClick]: () => {
                                pdfJsPrint();
                                logSxSPrint(viewState, getExtraCustomDataForDatapoint);
                            },
                            [FilterableMenuItemArgs.shouldShow]: () =>
                                viewState.previewPane &&
                                viewState.previewPane.mode === PreviewPaneMode.PdfJs &&
                                !viewState.previewPane.printDisabled,
                            [FilterableMenuItemArgs.ariaLabel]: loc(printAttachment),
                        })
                    );
                    break;
                case PreviewPaneCommandBarButton.ShowHideEmail:
                    menuItems.push(
                        new FilterableMenuItem({
                            [FilterableMenuItemArgs.name]:
                                stringOverrides?.showEmail || loc(showEmail_1),
                            [FilterableMenuItemArgs.icon]: ControlIcons.OpenPane,
                            [FilterableMenuItemArgs.onClick]: async () => {
                                const setRightPaneDisplayAndUpdateSize = await lazySetRightPaneDisplayAndUpdateSize.import();
                                setRightPaneDisplayAndUpdateSize(viewState, RightPaneDisplay.Show);
                                onResize();
                                logSxSShowHideEmail(
                                    viewState,
                                    true,
                                    getExtraCustomDataForDatapoint
                                );

                                const saveHideReadingPaneOption = await lazySaveHideReadingPaneOption.import();
                                saveHideReadingPaneOption(false);
                            },
                            [FilterableMenuItemArgs.shouldShow]: () =>
                                hasRightPane &&
                                (viewState.rightPaneDisplay === RightPaneDisplay.Hide ||
                                    (viewState.hideRightPane &&
                                        (viewState.rightPaneDisplay === RightPaneDisplay.Adaptive ||
                                            viewState.rightPaneDisplay === RightPaneDisplay.Show))),
                            [FilterableMenuItemArgs.displayOption]: useNewButtonOrder
                                ? DisplayOption.Right
                                : null,
                            [FilterableMenuItemArgs.ariaLabel]:
                                stringOverrides?.showEmail || loc(showEmail_1),
                            [FilterableMenuItemArgs.title]:
                                stringOverrides?.showEmail || loc(showEmail_1),
                        })
                    );
                    menuItems.push(
                        new FilterableMenuItem({
                            [FilterableMenuItemArgs.name]:
                                stringOverrides?.hideEmail || loc(hideEmail_1),
                            [FilterableMenuItemArgs.icon]: ControlIcons.ClosePane,
                            [FilterableMenuItemArgs.onClick]: async () => {
                                const setRightPaneDisplayAndUpdateSize = await lazySetRightPaneDisplayAndUpdateSize.import();
                                setRightPaneDisplayAndUpdateSize(viewState, RightPaneDisplay.Hide);
                                onResize();
                                logSxSShowHideEmail(
                                    viewState,
                                    false,
                                    getExtraCustomDataForDatapoint
                                );

                                const saveHideReadingPaneOption = await lazySaveHideReadingPaneOption.import();
                                saveHideReadingPaneOption(true);
                            },
                            [FilterableMenuItemArgs.shouldShow]: () =>
                                hasRightPane &&
                                (viewState.rightPaneDisplay === RightPaneDisplay.Adaptive ||
                                    viewState.rightPaneDisplay === RightPaneDisplay.Show) &&
                                !viewState.hideRightPane,
                            [FilterableMenuItemArgs.displayOption]: useNewButtonOrder
                                ? DisplayOption.Right
                                : null,
                            [FilterableMenuItemArgs.ariaLabel]:
                                stringOverrides?.hideEmail || loc(hideEmail_1),
                            [FilterableMenuItemArgs.title]:
                                stringOverrides?.hideEmail || loc(hideEmail_1),
                        })
                    );
                    break;
                default:
                    assertNever(item);
            }
        }
    });

    if (onPopOut) {
        menuItems.push(
            new FilterableMenuItem({
                [FilterableMenuItemArgs.icon]: ControlIcons.OpenInNewWindow,
                [FilterableMenuItemArgs.onClick]: onPopOut,
                [FilterableMenuItemArgs.shouldShow]: () => !!onPopOut,
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Right,
                [FilterableMenuItemArgs.ariaLabel]: loc(popoutsOpenInNewWindow),
                [FilterableMenuItemArgs.title]: loc(popoutsOpenInNewWindow),
            })
        );
    }

    if (!hideCloseButton) {
        menuItems.push(
            new FilterableMenuItem({
                [FilterableMenuItemArgs.icon]: ControlIcons.Cancel,
                [FilterableMenuItemArgs.onClick]: onClose,
                [FilterableMenuItemArgs.shouldShow]: () => !!onClose && !hideCloseButton,
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Right,
                [FilterableMenuItemArgs.ariaLabel]: loc(CloseText),
                [FilterableMenuItemArgs.title]: loc(CloseText),
            })
        );
    }

    return menuItems;

    function printImage() {
        lazyPrint.importAndExecute(getImageHtmlString(), getImageCSSString());
    }

    function getImageHtmlString(): string {
        const url = (viewState.previewPane as ImagePreviewState).previewUrl;
        const div = document.createElement('div');
        const img = document.createElement('img');
        img.src = url;
        div.appendChild(img);
        return div.innerHTML;
    }

    function getImageCSSString(): string {
        const imagecss = 'img{max-width:100%;max-height:100%;width:auto;}';
        return imagecss;
    }
}

export function getDownloadCommandItem(
    viewState: SxSViewState,
    getExtraCustomDataForDatapoint: null | (() => SxSCustomData),
    getDownloadUrl: null | (() => string),
    getContainerElementForDownload: null | (() => HTMLElement),
    getItemIdForLogging: null | (() => string),
    getCustomPropertiesForSigs?: null | (() => { FileType: string; Sender: string })
) {
    return new FilterableMenuItem({
        [FilterableMenuItemArgs.name]: loc(downloadText),
        [FilterableMenuItemArgs.icon]: ControlIcons.Download,
        [FilterableMenuItemArgs.onClick]: async () => {
            const downloadUrl = getDownloadUrl?.() || viewState.previewPane.downloadUrl;
            downloadFileFromUrl(
                downloadUrl,
                viewState.previewPane.downloadShouldOpenNewTab,
                getContainerElementForDownload ? getContainerElementForDownload() : null
            );

            logSxSCommandBarAction(
                'AttachmentDownload',
                {
                    mode: PreviewPaneMode[viewState.previewPane.mode],
                },
                getExtraCustomDataForDatapoint
            );

            const itemId = getItemIdForLogging?.();
            lazyLogSigsDatapoint.importAndExecute('SavedAttachment', {
                itemId: itemId && (await getImmutableEntryId(itemId)),
                customProperties: {
                    FileId: viewState.previewPane?.id?.Id,
                    Destination: 'Local',
                    ...getCustomPropertiesForSigs?.(),
                },
            });
        },
        [FilterableMenuItemArgs.shouldShow]: () =>
            viewState.previewPane && !!viewState.previewPane.downloadUrl,
        [FilterableMenuItemArgs.ariaLabel]: loc(downloadText),
    });
}

function logSxSShowHideEmail(
    viewState: SxSViewState,
    isShowEmail: boolean,
    getExtraCustomDataForDatapoint?: () => SxSCustomData
) {
    logSxSCommandBarAction(
        'SxSShowHideEmail',
        {
            mode: PreviewPaneMode[viewState.previewPane?.mode],
            didAutoHideEmail: viewState.didAutoHideRightPane,
            isShowEmail,
        },
        getExtraCustomDataForDatapoint
    );
}

function logSxSPrint(
    viewState: SxSViewState,
    getExtraCustomDataForDatapoint?: () => SxSCustomData
) {
    logSxSCommandBarAction(
        'SxSPrint',
        { mode: PreviewPaneMode[viewState.previewPane.mode] },
        getExtraCustomDataForDatapoint
    );
}

function isPdfPrintSupported(): boolean {
    return !isBrowserIE(); // IE does not support printing from an Iframe
}

function logSxSCommandBarAction(
    eventName: string,
    currentCustomData: CustomData,
    getExtraCustomDataForDatapoint?: () => SxSCustomData
) {
    const extraCustomData = getExtraCustomDataForDatapoint
        ? getExtraCustomDataForDatapoint()
        : null;
    logUsage(
        eventName,
        {
            ...currentCustomData,
            ...extraCustomData?.customData,
        },
        {
            cosmosOnlyData: extraCustomData && JSON.stringify(extraCustomData.cosmosOnlyData),
            isCore: eventName === 'SxSShowHideEmail',
        }
    );
}
